import { axiosInstance } from "./index";
import { TokenStorage } from "./tokenStorage";

export type NotificationTab = "all" | "creator" | "campaign" | "finance";

export interface PaymentNotificationContext {
	status: "completed" | "on_hold" | "failed" | "rejected_by_finance";
	statusLabel: "Paid" | "Delayed" | "Failed" | "Rejected by Finance";
	reason?: string;
	amount: number;
	currency: string;
	reference?: string;
	effectiveAt: string;
}

export interface PaymentStatementNotificationContext {
	statementNumber: string;
	amount: number;
	currency: string;
	submittedAt: string;
}

export interface NotificationItem {
	id: string;
	type: string;
	tab: Exclude<NotificationTab, "all">;
	priority: "low" | "medium" | "high";
	title: string;
	body: string;
	ctaLabel: string;
	ctaPath: string;
	tag?: string;
	campaignName?: string;
	actorName?: string;
	actorInitials?: string;
	actorAvatarUrl?: string;
	payment?: PaymentNotificationContext;
	paymentStatement?: PaymentStatementNotificationContext;
	readAt: string | null;
	createdAt: string;
}

export interface NotificationListResponse {
	items: NotificationItem[];
	nextCursor: string | null;
}

export interface NotificationCounts {
	totalUnread: number;
	creatorUnread: number;
	campaignUnread: number;
	financeUnread: number;
}

export interface FeatureFlags {
	notificationsEnabled: boolean;
}

const notificationsBaseURL =
	(import.meta.env.VITE_NOTIFICATIONS_API_URL as string | undefined)?.replace(/\/$/, "") || "";

async function notificationsRequest<T>(
	method: "get" | "patch",
	path: string,
	options?: { params?: Record<string, string | number | undefined>; data?: unknown },
): Promise<T> {
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
	};
	const accessToken = TokenStorage.getAccessToken();
	if (accessToken) {
		headers.Authorization = `Bearer ${accessToken}`;
	}

	if (notificationsBaseURL) {
		const { data } = await axiosInstance.request<T>({
			baseURL: notificationsBaseURL,
			url: path,
			method,
			headers,
			params: options?.params,
			data: options?.data,
			withCredentials: false,
		});
		return data;
	}

	const { data } = await axiosInstance.request<T>({
		url: path,
		method,
		headers,
		params: options?.params,
		data: options?.data,
	});
	return data;
}

export const notificationsAPI = {
	getFeatureFlags: async (): Promise<FeatureFlags> => {
		// Feature flags live on the main backend (AppConfig proxy).
		const { data } = await axiosInstance.get<FeatureFlags>("/feature-flags");
		return data;
	},

	getCounts: () => notificationsRequest<NotificationCounts>("get", "/notifications/counts"),

	list: (tab: NotificationTab = "all", cursor?: string | null, limit = 50) =>
		notificationsRequest<NotificationListResponse>("get", "/notifications", {
			params: {
				tab,
				limit,
				cursor: cursor || undefined,
			},
		}),

	markRead: (notificationId: string) =>
		notificationsRequest<NotificationItem>("patch", `/notifications/${encodeURIComponent(notificationId)}/read`),

	markAllRead: (tab: NotificationTab = "all", cursor?: string | null) =>
		notificationsRequest<{ processed: number; nextCursor: string | null }>("patch", "/notifications/read-all", {
			data: { tab, cursor: cursor || undefined },
		}),
};
